import { KENALL } from '@ken-all/kenall';
import type { Address } from '@ken-all/kenall/built/v1/interfaces.v20221101';
import { until } from '@vueuse/core';
import { keyByFile } from 'theme/utils/keyByFile';
import { captureException } from '~/utils/sentry-workaround';

const KENALL_API_KEY = '97e1cc62e74a0b3e66917bcc4bf6a2f82b0da51c';

// デフォルト1秒でのタイムアウトによるエラーが何件か起きていたので、タイムアウト時間を延ばす
const api = new KENALL(KENALL_API_KEY, { timeout: 3 * 1000 });

const FETCHING: unique symbol = Symbol('fetching');
const INVALID: unique symbol = Symbol('invalid');

const useKenAllResultStore = () =>
  useState<{ [key: string]: typeof FETCHING | typeof INVALID | Address[] }>(
    keyByFile(import.meta, 'kenallresults'),
    () => ({})
  );
const useFetchKenAll = () => {
  const { logger } = useLogger();

  const resultsStore = useKenAllResultStore();

  const fetch = async (postalCode: string) => {
    // すでに取得中の場合は何もしない
    if (resultsStore.value[postalCode] === FETCHING) return;
    // すでに結果がある場合は何もしない。郵便番号はそんなに変わらないので
    if (
      Array.isArray(resultsStore.value[postalCode]) ||
      resultsStore.value[postalCode] === INVALID
    )
      return;

    resultsStore.value = {
      ...resultsStore.value,
      [postalCode]: FETCHING,
    };

    if (!/^\d{7}$/.test(postalCode)) {
      resultsStore.value = {
        ...resultsStore.value,
        [postalCode]: INVALID,
      };
      return;
    }
    const { data } = await api
      .getAddress(postalCode, undefined, '2023-09-01')
      .catch((e) => {
        logger.value?.event.inputPostalCode({
          postalCode: postalCode,
          extra: `error: kenall. ${e}`,
        });
        captureException(`error: kenall. ${e}, postalCode: ${postalCode}`);
        return { data: INVALID } as const;
      });

    resultsStore.value = {
      ...resultsStore.value,
      [postalCode]: data,
    };
  };

  return {
    fetch,
  };
};

/**
 * 郵便番号から住所を取得する。郵便番号のrefからcomputedのアップデートと非同期関数の呼び出しを両方同時に走らせたかったので、
 * 同一のリクエストが複数走らない & 結果をキャッシュするようなストアにしている。
 */

// 郵便番号のrefから取得結果のcomputedを返す
export const usePostalCodeResult = (
  reactivePostalCode: RefOrGetterFunction<Nullable<string>>
) => {
  const { fetch } = useFetchKenAll();
  watch(
    reactivePostalCode,
    async (newPostalCode) => {
      if (newPostalCode == null) return;
      await fetch(newPostalCode);
    },
    {
      immediate: true,
    }
  );

  const resultsStore = useKenAllResultStore();
  const result = computed(() => {
    const postalCode = toValue(reactivePostalCode);
    if (postalCode == null) return null;
    const result = resultsStore.value[postalCode];
    if (result === FETCHING) return null;
    if (result === INVALID) return null;
    return result;
  });

  return { result };
};

// 郵便番号の取得を行う非同期関数を返す
export const useFetchPostalCodeResult = () => {
  const { fetch } = useFetchKenAll();

  const resultsStore = useKenAllResultStore();

  const fetchPostalCodeResult = async (postalCode: string) => {
    await fetch(postalCode);
    await until(resultsStore).toMatch(
      (v) => {
        return v[postalCode] !== FETCHING;
      },
      {
        deep: true,
        timeout: 200,
      }
    );

    const result = resultsStore.value[postalCode];
    if (result === INVALID) return null;
    if (result === FETCHING) return null;
    return result;
  };

  return { fetchPostalCodeResult };
};
